import React, { useEffect, useMemo } from "react";
import Chart from "react-apexcharts";
import { useStat } from "../../../DataStore/StatContext";
import { numberFormat } from "../../../util/araknerdlib";
import { useCurrency } from "../../../util/SystemVariables";
import { Skeleton, SkeletonCircle, SkeletonDonut } from "../../../util/skeleton/Skeleton";
import FinanceCardHead from "../FinanceCardHead";

/**
 * Theme notes:
 * - Uses Indigo palette (no greens)
 * - Header/value/subtitle provided by FinanceCardHead
 * - Legend/list stays compact + scrollable
 */

const INDIGO_PALETTE = [
  "#4F46E5",
  "rgba(79,70,229,.85)",
  "rgba(79,70,229,.70)",
  "rgba(79,70,229,.55)",
  "rgba(79,70,229,.40)",
  "rgba(79,70,229,.28)",
  "rgba(79,70,229,.20)",
  "rgba(79,70,229,.14)",
  "rgba(79,70,229,.10)",
  "rgba(79,70,229,.08)",
];

const ExpensesDonutCard = () => {
  const { topExpenses, loadingTopExpenses } = useStat();
  const currency = useCurrency();

  const { series, labels, total } = useMemo(() => {
    const listing = topExpenses?.top_listing ?? [];
    const s = listing.map((x) => Number(x.balance) || 0);
    const l = listing.map((x) => x.account_name);
    const t = Number(topExpenses?.total_expenses) || s.reduce((a, b) => a + b, 0);
    return { series: s, labels: l, total: t };
  }, [topExpenses]);

  const top10 = useMemo(() => (topExpenses?.top_listing ?? []).slice(0, 10), [topExpenses]);

  const options = useMemo(
    () => ({
      chart: { type: "donut", sparkline: { enabled: true } },
      labels,
      legend: { show: false },
      dataLabels: { enabled: false },
      stroke: { width: 0 },
      colors: INDIGO_PALETTE.slice(0, Math.max(3, labels.length)),
      tooltip: {
        y: { formatter: (v) => `${currency} ${numberFormat(v || 0)}` },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "78%",
            labels: {
              show: true,
              name: { show: false },
              value: {
                show: true,
                fontSize: "16px",
                fontWeight: 900,
                color: "#0F172A",
                formatter: () => `${currency} ${numberFormat(total)}`,
              },
              total: {
                show: true,
                label: "Total expenses",
                fontSize: "12px",
                fontWeight: 800,
                color: "rgba(71,85,105,.85)",
                formatter: () => "",
              },
            },
          },
        },
      },
    }),
    [labels, currency, total]
  );

  const isLoading = loadingTopExpenses || !topExpenses;

  /* ---------------- Skeleton ---------------- */
  if (isLoading) {
    return (
      <div className="yx-card mb-3">
        <div className="yx-card-head">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <Skeleton w="140px" h={14} />
            <Skeleton w="160px" h={12} />
          </div>
          <Skeleton w="220px" h={26} />
          <Skeleton w="120px" h={12} style={{ marginTop: 8 }} />
        </div>

        <div className="yx-card-body">
          <div className="row no-gutters align-items-center">
            <div className="col-6 pr-3 overflow-auto" style={{ maxHeight: 170 }}>
              {[...Array(7)].map((_, i) => (
                <div key={i} className="d-flex align-items-start mb-2">
                  <SkeletonCircle size={10} />
                  <div style={{ marginLeft: 10, width: "100%" }}>
                    <Skeleton w="70%" h={14} />
                    <Skeleton w="85%" h={12} style={{ marginTop: 6 }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="col-6 d-flex justify-content-center">
              <SkeletonDonut size={180} />
            </div>
          </div>
        </div>

        <div className="yx-card-foot d-flex justify-content-between">
          <Skeleton w="140px" h={12} />
          <Skeleton w="110px" h={12} />
        </div>
      </div>
    );
  }

  return (
    <FinanceCardHead
      title="Expenses"
      right={
        <span className="yx-pill yx-pill--muted">
          Current FY <i className="fa fa-chevron-down ml-1" />
        </span>
      }
      value={total}
      currency={currency}
      subtitle="Total expenses"
      formatValue={(v) => `${currency} ${numberFormat(v)}`}
    >
      <div className="row no-gutters align-items-center">
        {/* Left list */}
        <div className="col-12 col-md-6 pr-md-3">
          <div className="yx-expList overflow-auto" style={{ maxHeight: 175 }}>
            {top10.length === 0 ? (
              <div className="text-muted small">No expense data</div>
            ) : (
              top10.map((item, idx) => (
                <div key={item.account_id || idx} className="yx-expItem">
                  <span
                    className="yx-expDot"
                    style={{ background: INDIGO_PALETTE[idx % INDIGO_PALETTE.length] }}
                  />
                  <div className="yx-expItemText">
                    <div className="yx-expAmt">
                      {currency} {numberFormat(Number(item.balance) || 0)}
                    </div>
                    <div className="yx-expName" title={item.account_name}>
                      {item.account_name}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right donut */}
        <div className="col-12 col-md-6 d-flex justify-content-center">
          <div className="exp-donutWrap">
            <Chart
              options={options}
              series={series.length ? series : [1]}
              type="donut"
              height={150}
            />
          </div>
        </div>
      </div>

      <div className="yx-divider" />

      <div className="d-flex align-items-center justify-content-between">
        <div className="yx-sub" style={{ margin: 0 }}>
          Top categories: <strong>{Math.min(10, labels.length)}</strong>
        </div>
        <div className="yx-sub" style={{ margin: 0 }}>
          {labels.length > 10 ? `+${labels.length - 10} more` : " "}
        </div>
      </div>

    </FinanceCardHead>
  );
};


export default ExpensesDonutCard;
